/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { memo, useState, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line no-unused-vars
import { jsx, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import AkQuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import { ModalTransition } from '@atlaskit/modal-dialog';

import { LazyDevToolsDialog } from '@confluence/dev-tools';
import { LazyGlobalFeedbackCollector } from '@confluence/global-feedback-collector/entry-points/GlobalFeedbackCollector.ts';
import { FeedbackSource } from '@confluence/global-feedback-collector/entry-points/feedbackSourceType.ts';

import { HelpMenu } from './HelpMenu.tsx';

export type HelpWidgetProps = {
	contentId: string;
	openHelpDialog: () => void;
	isNCSEditor?: boolean;
	isTemplateEditor?: boolean;
	isCompanyHub?: boolean;
	isContentCreationPanelOpen?: boolean;
	isOpen?: boolean;
	setIsOpen?: (isOpen: boolean) => void;
};

const styles = cssMap({
	root: {
		position: 'absolute',
		bottom: token('space.300'),
		left: token('space.250'),
		zIndex: 400,
	},
});

const i18n = defineMessages({
	helpName: {
		id: 'editor-help.help.name',
		defaultMessage: 'Help',
		description:
			'The text is shown as the label for the help menu button in the editor toolbar, which when clicked opens the editor help center or help widget panel.',
	},
});

export const HelpWidget = memo(
	({
		openHelpDialog,
		contentId,
		isNCSEditor,
		isTemplateEditor,
		isCompanyHub,
		setIsOpen,
		isOpen,
	}: HelpWidgetProps) => {
		const { formatMessage } = useIntl();
		const [shouldShowDevTools, setShouldShowDevTools] = useState(false);
		const [shouldShowGlobalFeedbackCollector, setShouldShowGlobalFeedbackCollector] =
			useState(false);
		const triggerRef = useRef<HTMLButtonElement>(null);

		const handleButtonClick = useCallback(
			(e) => {
				e.stopPropagation();
				setIsOpen?.(!isOpen);
			},
			[isOpen, setIsOpen],
		);
		return (
			<>
				<div css={styles.root} data-testid="help-widget">
					{isOpen && (
						<HelpMenu
							openHelpDialog={openHelpDialog}
							contentId={contentId}
							isNCSEditor={isNCSEditor}
							isTemplateEditor={isTemplateEditor}
							isCompanyHub={isCompanyHub}
							isOpen={isOpen}
							setIsOpen={setIsOpen}
							setShouldShowDevTools={setShouldShowDevTools}
							setShouldShowGlobalFeedbackCollector={setShouldShowGlobalFeedbackCollector}
							triggerRef={triggerRef}
						/>
					)}
					<AkTooltip content={formatMessage(i18n.helpName)} position="right" hideTooltipOnClick>
						<IconButton
							ref={triggerRef}
							icon={AkQuestionCircleIcon}
							shape="circle"
							appearance="subtle"
							label={formatMessage(i18n.helpName)}
							testId="help-menu-icon-button"
							onClick={handleButtonClick}
							isSelected={isOpen}
							aria-expanded={isOpen}
							aria-haspopup="menu"
							color="color.icon.subtlest"
						/>
					</AkTooltip>
				</div>

				<ModalTransition>
					{shouldShowDevTools && (
						<LazyDevToolsDialog onClose={() => setShouldShowDevTools(false)} />
					)}
				</ModalTransition>
				<ModalTransition>
					{shouldShowGlobalFeedbackCollector && (
						<LazyGlobalFeedbackCollector
							feedbackSource={FeedbackSource.EDITOR}
							onClose={() => setShouldShowGlobalFeedbackCollector(false)}
							onCloseComplete={() => {}}
							contentType="page" // TODO: add contentType
						/>
					)}
				</ModalTransition>
			</>
		);
	},
);
